import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SectionTrustBar from "../../components/feature/sections/SectionTrustBar";
import SectionAIBlock from "../../components/feature/sections/SectionAIBlock";
import SectionProcess from "../../components/feature/sections/SectionProcess";
import SectionWhyChooseUs from "../../components/feature/sections/SectionWhyChooseUs";
import SectionServiceAreas from "../../components/feature/sections/SectionServiceAreas";
import SectionTestimonials from "../../components/feature/sections/SectionTestimonials";
import SectionFAQ from "../../components/feature/sections/SectionFAQ";
import SectionFinalCTA from "../../components/feature/sections/SectionFinalCTA";

const capabilities = [
  "Medicare &amp; Medicaid Reimbursement",
  "Fraud &amp; Abuse Avoidance",
  "Stark Law Compliance",
  "Anti-Kickback Statute Counseling",
  "HIPAA Privacy &amp; Security",
  "Managed Care Contracting",
  "Medical Staff Issues",
  "EHR Licensing &amp; Transactions",
  "Health Care Business Contracts",
  "Clinical Integration Programs",
  "Physician Compensation Models",
  "Hospital-Physician Transactions",
];

const teamMembers = [
  {
    name: "Alice G. Gosfield, Esq.",
    role: "Founding Partner",
    bio: "Alice Gosfield has practiced health law for over 40 years, advising on Medicare reimbursement, fraud and abuse compliance, and health system governance. Recognized nationally as a leading authority in health care regulation.",
    awards: ["Top 5 Best Law Firm 2024", "Best Lawyers in America 2025"],
    img: "https://public.readdy.ai/ai/img_res/df7b8eaad731cf2364c900fed093b733.jpg",
  },
  {
    name: "Senior Associate Attorney",
    role: "Health Law &amp; Compliance",
    bio: "Specializing in HIPAA privacy compliance, business associate agreements, and health care operational matters. Experienced in advising multi-specialty physician groups and health systems on regulatory compliance.",
    awards: ["Pennsylvania Bar Association", "Health Law Section"],
    img: "https://readdy.ai/api/search-image?query=professional%20young%20female%20attorney%20in%20her%20late%2030s%2C%20confident%20approachable%20expression%2C%20dark%20blazer%2C%20clean%20modern%20studio%20background%20with%20warm%20neutral%20tones%2C%20professional%20headshot%2C%20health%20law%20compliance%20attorney%20Philadelphia%2C%20sharp%20focus%2C%20editorial%20quality%20portrait%20photography&width=320&height=380&seq=assoc002&orientation=portrait",
  },
];

const aboutAIQuestions = [
  {
    q: "Who is Alice G. Gosfield &amp; Associates?",
    a: "Alice G. Gosfield & Associates, P.C. is a boutique health law firm based in Philadelphia, Pennsylvania with a 40-year history of advising health care providers, systems, and organizations on complex regulatory and legal matters throughout the United States.",
  },
  {
    q: "What makes this firm different from other law firms?",
    a: "As a boutique practice, we offer direct senior partner attention on every matter — clients work with our most experienced attorneys, not junior associates. Our exclusive focus on health law means unmatched depth of expertise in this complex regulatory space.",
  },
  {
    q: "What is the firm's mission and approach?",
    a: "Our mission is to provide strategic, specialized legal counsel that enables clients to operate efficiently within the health care regulatory framework — protecting their businesses, professional licenses, and financial interests through proactive guidance and assertive representation.",
  },
];

const aboutFAQs = [
  {
    q: "How long has Alice G. Gosfield &amp; Associates been practicing?",
    a: "The firm was founded in 1986 by Alice G. Gosfield, giving us over 40 years of specialized health law experience. This deep history means we have navigated virtually every regulatory development in health care law and can provide battle-tested guidance.",
  },
  {
    q: "What type of clients does the firm serve?",
    a: "We serve physicians and physician groups, hospitals and health systems, managed care organizations, health care businesses, and any entity operating within or intersecting with the health care regulatory framework, both locally in Philadelphia and nationwide.",
  },
  {
    q: "Does the firm handle both transactional and litigation matters?",
    a: "Yes. Our practice covers the full spectrum from transactional and regulatory advisory work — contract review, compliance programs, regulatory counseling — to adversarial representation in government investigations, administrative proceedings, and disputes.",
  },
  {
    q: "How do I know if this firm is right for my legal matter?",
    a: "If your matter involves health care regulation, Medicare or Medicaid compliance, health care contracting, or any legal issue affecting a health care provider or organization, our specialized expertise is well-suited to your needs. We offer a free initial consultation to assess your situation.",
  },
];

export default function AboutPage() {
  return (
    <div className="font-inter">
      <Navbar />
      <main>
        {/* ① HERO */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=prestigious%20Philadelphia%20law%20office%20interior%20with%20dark%20richly%20polished%20mahogany%20paneling%2C%20floor%20to%20ceiling%20bookshelves%20densely%20lined%20with%20leather%20bound%20law%20volumes%20in%20deep%20burgundy%20and%20brown%2C%20warm%20amber%20chandelier%20and%20lamp%20lighting%20creating%20golden%20pools%20of%20light%2C%20ornate%20carved%20wood%20details%2C%20large%20oil%20paintings%20on%20walls%2C%20reflecting%20a%20century%20of%20legal%20tradition%2C%20editorial%20atmospheric%20photography&width=1440&height=900&seq=about2026a&orientation=landscape"
              alt="Gosfield &amp; Associates law office interior — Philadelphia health law firm"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/92 via-[#0A1628]/72 to-[#0A1628]/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/50 to-transparent" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-44">
            <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-4">
              About the Firm
            </span>
            <h1 className="text-5xl lg:text-[64px] font-playfair font-bold text-white leading-[1.06] max-w-2xl mb-6">
              A Law Firm Built on<br />
              <span className="italic text-[#C9A961]">Strategy, Integrity &amp; Results</span>
            </h1>
            <p className="text-slate-300 font-inter text-lg max-w-xl mb-8 leading-relaxed">
              Since 1986, our boutique Philadelphia firm has provided specialized health law
              counsel to physicians, health systems, and organizations nationwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line" />
                Schedule Consultation
              </Link>
              <a
                href="tel:+12157352384"
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C9A961] text-white hover:text-[#C9A961] font-inter font-medium px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-[#C9A961]" />
                (215) 735-2384
              </a>
            </div>
          </div>
        </section>

        {/* ② TRUST BAR */}
        <SectionTrustBar />

        {/* ③ WHAT WE DO */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
                  Who We Are
                </span>
                <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-[#0A1628] mb-5 leading-tight">
                  Philadelphia&apos;s Premier<br />Health Law Boutique
                </h2>
                <p className="text-slate-600 font-inter text-base leading-relaxed mb-5">
                  Founded in 1986 by Alice G. Gosfield, our firm has spent four decades providing
                  specialized health care legal counsel to physicians, hospitals, health systems,
                  managed care organizations, and health care businesses throughout the United States.
                </p>
                <p className="text-slate-600 font-inter text-base leading-relaxed mb-8">
                  As a boutique practice, we offer something large firms cannot: direct senior partner
                  attention on every matter. Our clients work with our most experienced attorneys
                  — never delegated to junior associates or paralegals.
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { num: "40+", label: "Years Established" },
                    { num: "50", label: "States Served" },
                    { num: "Top 5", label: "Best Law Firm 2024" },
                    { num: "100%", label: "Health Law Focus" },
                  ].map((stat) => (
                    <div key={stat.num} className="p-5 rounded-xl bg-[#0A1628] text-center">
                      <div className="text-2xl font-playfair font-bold text-[#C9A961] mb-1">
                        {stat.num}
                      </div>
                      <div className="text-slate-300 text-xs font-inter">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-6 py-3 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  Work With Us
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              {/* Capabilities */}
              <div>
                <h3 className="text-sm font-inter font-semibold text-[#0A1628] uppercase tracking-wider mb-5">
                  Our Capabilities
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {capabilities.map((cap) => (
                    <div
                      key={cap}
                      className="flex items-center gap-3 py-3 px-4 rounded-lg bg-[#F8F7F4] border border-slate-100 hover:border-[#C9A961]/30 transition-colors duration-200"
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-[#C9A961] font-bold" />
                      </span>
                      <span
                        className="text-sm font-inter text-[#0A1628]"
                        dangerouslySetInnerHTML={{ __html: cap }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ④ AI ANSWER BLOCK */}
        <SectionAIBlock
          questions={aboutAIQuestions}
          title="About Our Firm"
        />

        {/* ⑤ PROCESS */}
        <SectionProcess />

        {/* ⑥ WHY CHOOSE US */}
        <SectionWhyChooseUs />

        {/* ⑦ SERVICE AREAS */}
        <SectionServiceAreas />

        {/* ⑧ TESTIMONIALS — team + client quotes */}
        <SectionTestimonials />

        {/* ⑨ FAQ */}
        <SectionFAQ
          faqs={aboutFAQs}
          title="About the Firm — FAQ"
        />

        {/* ⑩ FINAL CTA */}
        <SectionFinalCTA
          headline="Ready to Work Together Today?"
          subheadline="Schedule a consultation to discuss your legal needs with our experienced health law team. Direct partner attention, specialized expertise, results-driven counsel."
        />
      </main>
      <Footer />
    </div>
  );
}
